import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {ApiError, newIdempotencyKey, uuidv7} from '../api';
import type {ApiClient} from '../api';
import type {
  NewOrderEvent,
  Order,
  ServiceMode,
  SubmitOrderRequest,
} from '../api/types';
import {StorageKeys, readJson, remove, writeJson} from '../app/storage';
import {useCart} from '../cart';
import {useGuestSession} from '../session';
import {useStreamEvents} from '../stream';

/**
 * Submitting an order, and reading it back afterwards.
 *
 * ## The single most important write in the system
 *
 * `POST /v1/orders` is the write most likely to be retried by a phone that never
 * saw the response — the guest presses send, the 4G drops, the request either
 * arrived or did not, and nobody knows which. Three layers make that safe and
 * **this file uses all three**:
 *
 * 1. `order_id` is minted once, written to storage, and reused for every
 *    attempt. The same id returns the existing order instead of a second one.
 * 2. `Idempotency-Key` is minted once alongside it and reused. A replay returns
 *    the original response body.
 * 3. Each `line_id` is client-minted, so a partially-applied batch converges.
 *
 * The consequence for design: **pressing send again is always safe**, and Q7's
 * copy says so (`q7.connectionLostAction`). That is the whole reason a guest
 * retries instead of waving at a server.
 *
 * ## Nothing here decides what anything costs
 *
 * Money fields are not sent and would be ignored if they were. `Order.totals`
 * comes back priced by the server, and that is the only total this surface ever
 * shows as a total.
 *
 * ## Payment is still not here, and now that is a choice rather than a blocker
 *
 * The checkout endpoints stopped being PLACEHOLDER on 2026-08-11 and are wired
 * up in `ApiClient` — but nothing in this file calls them, because a checkout is
 * a screen-local intent (Q8) with its own idempotency record on disk, not a
 * property of the order this phone submitted. `submit()` sends the order to the
 * kitchen; what happens to the money afterwards is Q8's and Q9's.
 */

export type SubmitStatus = 'idle' | 'submitting' | 'sent' | 'failed';

/** Persisted so a reload mid-submit reuses the same identity rather than minting a new one. */
interface Submission {
  order_id: string;
  idempotency_key: string;
  client_seq: number;
}

export interface OrderValue {
  submitStatus: SubmitStatus;
  /** The order this phone is currently attached to, if any. */
  order: Order | null;
  error: ApiError | null;
  /**
   * Send the cart. Idempotent — calling it again after a failure retries the
   * *same* submission, it does not create a second order.
   */
  submit: (options?: {note?: string | null}) => Promise<Order | null>;
  /** Read an order by id. Q17 uses this to survive a refresh and a returning guest. */
  loadOrder: (orderId: string) => Promise<Order>;
  /** Re-read the current order. What a stream frame is telling you to do. */
  refresh: () => Promise<void>;
  /** Order ids this phone has submitted, newest last. */
  knownOrderIds: string[];
  /** Start a fresh submission after one has been sent (a second round). */
  reset: () => void;

  /**
   * Ask for somebody to come to the table.
   *
   * Commutative and unauthenticated beyond the table token, so pressing it
   * twice is not an error and not a second call — the server keeps the FIRST
   * unanswered one, which is what lets the floor sort by who has waited longest.
   *
   * Resolves false when there is no order to hang the call on. A guest who has
   * not ordered yet has nothing to call about that this surface could route.
   */
  callStaff: () => Promise<boolean>;

  /**
   * Ask for the bill: `open -> awaiting_payment`.
   *
   * **The one event this surface sends that needs `If-Match`** — it is a
   * lifecycle transition, so it does not commute, and the token is the order's
   * `seq` as last read. A `412` means somebody moved the ticket underneath
   * (a second round from another phone, the till taking payment) and the cue is
   * to re-read, not to retry.
   *
   * The contract has allowed this from `guest_web` since the freeze. Nothing
   * here ever sent one.
   */
  requestBill: () => Promise<boolean>;
}

const OrderContext = createContext<OrderValue | null>(null);

function loadSubmission(): Submission {
  const existing = readJson<Submission>(StorageKeys.submission);
  if (existing?.order_id && existing.idempotency_key) return existing;
  const fresh: Submission = {
    order_id: uuidv7(),
    idempotency_key: newIdempotencyKey(),
    client_seq: 0,
  };
  writeJson(StorageKeys.submission, fresh);
  return fresh;
}

export function OrderProvider({children}: {children: ReactNode}) {
  const {session, client, status: sessionStatus} = useGuestSession();
  const cart = useCart();

  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [order, setOrder] = useState<Order | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [knownOrderIds, setKnownOrderIds] = useState<string[]>(
    () => readJson<string[]>(StorageKeys.orders) ?? [],
  );

  const submission = useRef<Submission | null>(null);

  const rememberOrderId = useCallback((orderId: string) => {
    setKnownOrderIds((current) => {
      if (current.includes(orderId)) return current;
      const next = [...current, orderId].slice(-20);
      writeJson(StorageKeys.orders, next);
      return next;
    });
  }, []);

  const loadOrder = useCallback(
    async (orderId: string): Promise<Order> => {
      const loaded = await client.getOrder(orderId);
      rememberOrderId(orderId);
      return loaded;
    },
    [client, rememberOrderId],
  );

  const refresh = useCallback(async () => {
    const id = order?.order_id ?? session?.active_order_id ?? null;
    if (!id) return;
    try {
      setOrder(await loadOrder(id));
    } catch (caught) {
      // Offline is normal. Keep whatever is on screen and let Q14 say it may be
      // stale rather than blanking a status a guest is reading.
      if (caught instanceof ApiError && caught.kind === 'network') return;
      throw caught;
    }
  }, [order?.order_id, session?.active_order_id, loadOrder]);

  // The table may already have an open order — another phone at the same table
  // started it, or this one did before a reload. `active_order_id` is how a
  // group cart converges on one ticket.
  const attached = useRef<string | null>(null);
  useEffect(() => {
    const id = session?.active_order_id;
    if (sessionStatus !== 'active' || !id || attached.current === id) return;
    attached.current = id;
    loadOrder(id)
      .then(setOrder)
      .catch(() => {
        // A 404 here means the ticket closed. Not an error to show anybody.
      });
  }, [sessionStatus, session?.active_order_id, loadOrder]);

  // A pushed frame is a hint to re-read, never truth — especially about money.
  useStreamEvents((event) => {
    if (!event.order_id) return;
    const current = order?.order_id ?? session?.active_order_id;
    if (event.order_id !== current) return;
    // Already at or past this position: nothing to fetch.
    if (order && event.order_seq !== null && event.order_seq !== undefined) {
      if (order.seq >= event.order_seq) return;
    }
    void refresh();
  });

  const submit = useCallback(
    async (options?: {note?: string | null}): Promise<Order | null> => {
      if (!session) return null;
      if (cart.lines.length === 0) return null;

      submission.current ??= loadSubmission();
      const current = submission.current;

      setSubmitStatus('submitting');
      setError(null);

      const serviceMode: ServiceMode = session.table_id ? 'dine_in' : 'queue_ahead';

      const request: SubmitOrderRequest = {
        order_id: current.order_id,
        // `channel` is forced to `guest_qr` by the client. Not a parameter.
        channel: 'guest_qr',
        service_mode: serviceMode,
        table_id: session.table_id ?? null,
        submit: true,
        lines: cart.toOrderLines(),
        note: options?.note ?? null,
        client_seq: current.client_seq,
        // The device's own belief about when this happened. Informational — the
        // server orders by its own clock and may clamp this. A wrong phone clock
        // must never cost a real order.
        occurred_at: new Date().toISOString(),
      };

      /*
       * ONE TABLE CARRIES ONE BILL, so a second round is an append, not a submit.
       *
       * `POST /v1/orders` refuses a second live order on the same table with
       * `409 conflict`, and the contract says what to do instead: "a guest phone
       * scanning an occupied table must open that ticket rather than a new one".
       * Until 2026-08-14 this provider only knew how to create, so the surface
       * could order **once per table, ever** — and it offered `สั่งเพิ่ม` on
       * three screens, every one of which walked into that wall. Found on the
       * first real scan at ฟ้าใส.
       *
       * **The order this phone is holding decides first, and the session second.**
       *
       * `session.active_order_id` is the server's answer to "is this table
       * occupied" as of the last session read — and a session is read **once, on
       * start** (`GuestSessionProvider.refresh` is guarded by `confirmed`). So
       * the phone that scans a *free* table carries `active_order_id: null` for
       * as long as it stays open, including through the round it just sent. That
       * is the shape the fix of 2026-08-14 missed: it only ever ran for a phone
       * that arrived at a table somebody else had opened, or after a reload.
       *
       * What the guest saw: the first round goes, the second one comes back
       * `409` — *"ร้านยังไม่รับออเดอร์นี้"* — because the round was sent as a
       * second `POST /v1/orders` on a table that now has a live bill. Which is
       * every table that has ordered once. Reported from the pilot, 2026-08-16.
       *
       * `order` is set the moment a submit lands and by the attach effect above,
       * so it is the same answer a moment sooner, and it is what `ask()` already
       * uses for the two button events.
       *
       * The submission identity is reused for both paths, and it has to be: the
       * `Idempotency-Key` replays the batch, and each event's client-minted
       * `event_id` deduplicates that event, so a retry after a timeout adds the
       * round once whichever call it landed in. The key is safe across the two
       * paths because the server scopes it by matched route, so the same key on
       * `POST /v1/orders` and on `.../events` is two records, not a replay.
       */
      const appendTo = order?.order_id ?? session.active_order_id ?? null;

      try {
        const created = await sendRound(client, appendTo, request, current);
        setOrder(created);
        rememberOrderId(created.order_id);
        setSubmitStatus('sent');
        // The lines are the server's now. Keeping them would let a guest send
        // the same round twice under a *new* order id, which no idempotency
        // layer can catch.
        cart.clear();
        remove(StorageKeys.submission);
        submission.current = null;
        return created;
      } catch (caught) {
        const failure =
          caught instanceof ApiError
            ? caught
            : new ApiError({kind: 'parse', message: String(caught)});
        setError(failure);
        setSubmitStatus('failed');
        // Deliberately keeps the submission identity: retrying reuses the same
        // `order_id` and the same `Idempotency-Key`, which is what makes
        // "send again" safe even when the first attempt did arrive.
        return null;
      }
    },
    [session, order?.order_id, cart, client, rememberOrderId],
  );

  const reset = useCallback(() => {
    remove(StorageKeys.submission);
    submission.current = null;
    setSubmitStatus('idle');
    setError(null);
  }, []);

  /**
   * Append one no-payload event to the order this phone is attached to.
   *
   * Shared by the two asks below because they differ in exactly two ways — the
   * type, and whether the order's `seq` rides along as `If-Match` — and writing
   * the batching, the fresh idempotency key and the re-read twice is how the two
   * come to disagree about one of them.
   *
   * **A fresh `event_id` and a fresh `Idempotency-Key` per press**, unlike a
   * round of lines: pressing the button again is a *new* ask, not a retry of the
   * old one. The server's own dedup is what makes a double press harmless.
   */
  const ask = useCallback(
    async (type: 'staff_called' | 'bill_requested'): Promise<boolean> => {
      const current = order;
      if (!client || !current) return false;
      const result = await client.appendOrderEvents(
        current.order_id,
        [
          {
            event_id: uuidv7(),
            client_seq: 0,
            type,
            occurred_at: new Date().toISOString(),
          },
        ],
        newIdempotencyKey(),
        undefined,
        // Only the transition needs one. Sending it on the commutative event
        // would turn a second phone's round into a `412` on a button press that
        // does not conflict with anything.
        type === 'bill_requested' ? String(current.seq) : null,
      );
      setOrder(result.order);
      return true;
    },
    [client, order],
  );

  const callStaff = useCallback(() => ask('staff_called'), [ask]);
  const requestBill = useCallback(() => ask('bill_requested'), [ask]);

  const value = useMemo<OrderValue>(
    () => ({
      submitStatus,
      order,
      error,
      submit,
      loadOrder,
      refresh,
      knownOrderIds,
      reset,
      callStaff,
      requestBill,
    }),
    [
      submitStatus,
      order,
      error,
      submit,
      loadOrder,
      refresh,
      knownOrderIds,
      reset,
      callStaff,
      requestBill,
    ],
  );

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
}

/**
 * Send one round of lines, whichever call the table's state makes it.
 *
 * ## Why the client is allowed to be wrong about this exactly once
 *
 * A phone cannot know whether its table is holding a live bill. Two things
 * change that answer without this phone doing anything: another phone at the
 * same table opens the ticket, and the till closes it. Both are ordinary, and
 * both are races no amount of local bookkeeping wins — the state lives on the
 * server and the guest presses send when they press send.
 *
 * So the caller's target is a *belief*, and the two ways it can be wrong each
 * have a server answer that names the mistake precisely:
 *
 *  * appending to a bill that has closed -> `not_found` (the guest is no longer
 *    allowed to read it, so the server will not say "closed")
 *  * opening a bill on a table that has one -> `conflict`
 *
 * Either one means "you took the other path", so the session is re-read — the
 * server's own answer, not a guess — and the round goes again the other way.
 * **Once.** A second refusal is a real refusal and is shown; looping here would
 * turn a table being cleared mid-tap into an infinite send.
 *
 * A guest who is bounced this way sees one send that works, which is the point:
 * neither of these is their mistake, and neither is worth an error panel that
 * asks them to fetch a member of staff.
 */
async function sendRound(
  client: ApiClient,
  appendTo: string | null,
  request: SubmitOrderRequest,
  submission: Submission,
): Promise<Order> {
  const attempt = async (target: string | null): Promise<Order> =>
    target
      ? appendRound(client, target, request, submission)
      : (await client.submitOrder(request, submission.idempotency_key)).order;

  try {
    return await attempt(appendTo);
  } catch (caught) {
    const tookTheWrongPath =
      caught instanceof ApiError &&
      (appendTo ? caught.code === 'not_found' : caught.code === 'conflict');
    if (!tookTheWrongPath) throw caught;

    let live: string | null;
    try {
      live = (await client.getGuestSession()).active_order_id ?? null;
    } catch {
      // The server could not be asked, so the refusal stands as it came. A
      // failure to diagnose is not a second failure to report.
      throw caught;
    }
    // Same answer as the belief that was just refused: the refusal was about
    // something else and re-sending would only earn it twice.
    if (live === appendTo) throw caught;
    return attempt(live);
  }
}

/**
 * A round of lines added to a bill that already exists.
 *
 * One event per line rather than one for the round, because `line_item_added`
 * is per line in the contract and because the server reports acceptance per
 * `event_id` — a round where one dish went 86 mid-tap comes back with that one
 * event rejected and the rest applied, instead of the whole round failing.
 *
 * `client_seq` climbs within the batch from the submission's own counter, which
 * is what lets the server reject a batch a device sent out of order.
 *
 * The kitchen note rides as a `note_added` on the order rather than on a line:
 * the guest wrote it about the round they just added, and the contract has no
 * per-round note anywhere else.
 */
async function appendRound(
  client: ApiClient,
  orderId: string,
  request: SubmitOrderRequest,
  submission: Submission,
): Promise<Order> {
  const lines = request.lines ?? [];
  const occurred = request.occurred_at;

  const events: NewOrderEvent[] = lines.map((line, index) => ({
    /*
     * **The line's own id, not a fresh one.** `event_id` is this event's
     * idempotency key, so a retry has to produce the SAME value or the round is
     * added twice — and a retry is the normal case here, because "send again" is
     * what a guest does when the first attempt timed out on 4G.
     *
     * `line_id` is already a client-minted UUID, minted once when the line
     * entered the cart and stable across restarts, which is exactly the property
     * needed. Minting one here with `uuidv7()` would look right and would defeat
     * the second idempotency layer entirely, leaving only the `Idempotency-Key`
     * header between a slow connection and a double order.
     */
    event_id: line.line_id,
    client_seq: submission.client_seq + index,
    type: 'line_item_added',
    occurred_at: occurred,
    payload: line as unknown as Record<string, unknown>,
  }));

  if (request.note) {
    events.push({
      // Stable for the same reason, and derived from the submission rather than
      // from a line: the note belongs to the round, and the round is the
      // submission.
      event_id: submission.order_id,
      client_seq: submission.client_seq + events.length,
      type: 'note_added',
      occurred_at: occurred,
      payload: {note: request.note},
    });
  }

  const result = await client.appendOrderEvents(orderId, events, submission.idempotency_key);
  return result.order;
}

export function useOrder(): OrderValue {
  const value = useContext(OrderContext);
  if (!value) throw new Error('useOrder must be used inside <OrderProvider>');
  return value;
}
